import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2, AlertTriangle, ChevronLeft, Check, Lightbulb, Trash2, Plus } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/toastContext';

// ============================================================
//  一句话建 Agent（P6，对话式 Builder）
//  流程：输入描述 → LLM 推导草案 → 假设确认页（可修改关键字段）→ 确认创建 → 配置中心
// ============================================================

interface DraftSkill {
  name?: string;
  display_name: string;
  description?: string | null;
  when_to_use?: string | null;
  instructions?: string | null;
  category?: string;
  tags?: string[];
}

interface DraftPersona {
  identity?: { role?: string; domain?: string; experience?: string };
  mission?: { primary?: string; scope?: string[]; out_of_scope?: string[] };
  rules?: string[];
  communication?: { tone?: string; style?: string; example_phrases?: string[] };
  [key: string]: unknown;
}

interface Draft {
  name: string;
  description: string;
  domain: string | null;
  persona: DraftPersona;
  skills: DraftSkill[];
  assumptions: string[];
}

type Stage = 'input' | 'review';

function extractError(e: unknown): string {
  const err = e as { response?: { status?: number; data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (err.response?.status === 503) return '未配置 LLM，请先在「模型与通道」中添加 LLM 配置';
  if (typeof err.message === 'string') return err.message;
  return '未知错误';
}

export default function AgentBuilderOneShot() {
  const [stage, setStage] = useState<Stage>('input');
  const [description, setDescription] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const toast = useToast();

  // 第一步：推导草案（不落库）
  const handleGenerate = async () => {
    if (!description.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.post('/agents/build', { description: description.trim() });
      setDraft(res.data.draft);
      setStage('review');
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  // 第二步：确认创建（应用用户修改后落库）
  const handleConfirm = async () => {
    if (!draft) return;
    setCreating(true);
    setError(null);
    try {
      const res = await apiClient.post('/agents/build', {
        description: description.trim(),
        confirm: true,
        overrides: {
          name: draft.name,
          description: draft.description,
          domain: draft.domain,
          persona: draft.persona,
          skills: draft.skills,
        },
      });
      const agentId = res.data.created?.agent_id;
      toast.success('Agent 创建成功');
      if (agentId) {
        navigate(`/agents/${agentId}/config-center`);
      } else {
        navigate('/agents');
      }
    } catch (e) {
      setError(extractError(e));
    } finally {
      setCreating(false);
    }
  };

  const updateSkill = (idx: number, patch: Partial<DraftSkill>) => {
    if (!draft) return;
    const skills = [...draft.skills];
    skills[idx] = { ...skills[idx], ...patch };
    setDraft({ ...draft, skills });
  };

  const renderInput = () => (
    <div className="animate-fade-in">
      <div className="glass-card" style={{ padding: '32px', textAlign: 'center' }}>
        <Sparkles size={40} style={{ color: 'var(--neon-purple, #a78bfa)', marginBottom: '16px' }} />
        <h3 style={{ marginBottom: '8px' }}>一句话描述你想要的 Agent</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
          不用填表，直接说出需求。AI 会推导人设、初始技能，并列出全部假设供你修改。
        </p>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="例如：一个懂售后政策的客服专家"
          rows={3}
          style={{ width: '100%', marginBottom: '16px' }}
          disabled={loading}
        />
        {error && (
          <div
            className="glass-card"
            style={{ marginBottom: '16px', padding: '12px', borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
          >
            <AlertTriangle size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            {error}
          </div>
        )}
        <button className="btn btn-primary" onClick={handleGenerate} disabled={!description.trim() || loading}>
          {loading ? <Loader2 size={18} className="spin" /> : <Sparkles size={18} />}
          {loading ? '正在推导草案...' : '推导草案'}
        </button>
      </div>
    </div>
  );

  const renderReview = () => {
    if (!draft) return null;
    return (
      <div className="animate-fade-in">
        <h3 style={{ marginBottom: '16px' }}>确认草案与假设</h3>

        {/* 假设清单：逐条展示 */}
        <div className="glass-card" style={{ padding: '16px', marginBottom: '16px' }}>
          <div className="flex" style={{ alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <Lightbulb size={18} style={{ color: 'var(--neon-yellow, #facc15)' }} />
            <strong>AI 做出的假设（{draft.assumptions.length} 条）</strong>
          </div>
          <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--text-secondary)' }}>
            {draft.assumptions.map((a, i) => (
              <li key={i} style={{ marginBottom: '4px' }}>{a}</li>
            ))}
          </ul>
        </div>

        {/* 基础信息：可编辑 */}
        <div className="glass-card" style={{ padding: '16px', marginBottom: '16px' }}>
          <strong style={{ display: 'block', marginBottom: '10px' }}>基础信息</strong>
          <div className="flex flex-col" style={{ gap: '12px' }}>
            <div>
              <label className="form-label">名称</label>
              <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} maxLength={64} />
            </div>
            <div>
              <label className="form-label">描述</label>
              <textarea
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <label className="form-label">领域</label>
              <input
                value={draft.domain || ''}
                onChange={(e) => setDraft({ ...draft, domain: e.target.value })}
                placeholder="例如：客服、技术支持"
              />
            </div>
            <div className="grid grid-2" style={{ gap: '12px' }}>
              <div>
                <label className="form-label">人设角色</label>
                <input
                  value={draft.persona.identity?.role || ''}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      persona: { ...draft.persona, identity: { ...draft.persona.identity, role: e.target.value } },
                    })
                  }
                />
              </div>
              <div>
                <label className="form-label">语气</label>
                <input
                  value={draft.persona.communication?.tone || ''}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      persona: {
                        ...draft.persona,
                        communication: { ...draft.persona.communication, tone: e.target.value },
                      },
                    })
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* 初始技能：可编辑 */}
        <div className="glass-card" style={{ padding: '16px', marginBottom: '16px' }}>
          <strong style={{ display: 'block', marginBottom: '10px' }}>初始技能（{draft.skills.length} 个）</strong>
          <div className="flex flex-col" style={{ gap: '12px' }}>
            {draft.skills.map((skill, idx) => (
              <div key={idx} className="glass-card" style={{ padding: '12px' }}>
                <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <input
                    value={skill.display_name}
                    onChange={(e) => updateSkill(idx, { display_name: e.target.value })}
                    style={{ fontWeight: 600 }}
                  />
                  <button
                    className="icon-btn danger"
                    onClick={() => setDraft({ ...draft, skills: draft.skills.filter((_, i) => i !== idx) })}
                    disabled={draft.skills.length <= 1}
                    title={draft.skills.length <= 1 ? '至少保留一个技能' : '删除技能'}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <textarea
                  value={skill.instructions || ''}
                  onChange={(e) => updateSkill(idx, { instructions: e.target.value })}
                  placeholder="技能指令（可复用的自然语言指令）"
                  rows={2}
                />
              </div>
            ))}
            <button
              className="btn btn-secondary"
              onClick={() =>
                setDraft({
                  ...draft,
                  skills: [...draft.skills, { display_name: '', instructions: '', category: 'general' }],
                })
              }
              disabled={draft.skills.length >= 6}
            >
              <Plus size={16} /> 添加技能
            </button>
          </div>
        </div>

        {error && (
          <div
            className="glass-card"
            style={{ marginBottom: '16px', padding: '12px', borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
          >
            <AlertTriangle size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            {error}
          </div>
        )}

        <div className="flex" style={{ gap: '12px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={() => setStage('input')} disabled={creating}>
            <ChevronLeft size={18} /> 返回修改描述
          </button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={creating || !draft.name.trim() || draft.skills.length === 0}
          >
            {creating ? <Loader2 size={18} className="spin" /> : <Check size={18} />}
            {creating ? '正在创建...' : '确认创建 Agent'}
          </button>
        </div>
      </div>
    );
  };

  return stage === 'input' ? renderInput() : renderReview();
}
